import React, { useCallback, type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import { SELECT_VARIANT_WITH_CHECKBOX, Select } from 'mailup-web-components'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'
import useAsyncSelect from 'Hooks/useAsyncSelect'

import { changeSearchCallback } from 'Pages/GroupsOverlap/recoil/callbacks/callbacks'
import { searchState } from 'Pages/GroupsOverlap/recoil/state/state'
import {
  fetchGroupById,
  fetchGroups,
} from 'Pages/GroupsOverlap/services/services'

const InputTo: FC = () => {
  const intl = useIntl()

  const { to, toIsDirty } = useRecoilValue(searchState)

  const changeSearch = useRecoilCallback(changeSearchCallback, [])

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'groups-overlap-to',
      value: to,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchGroups(''),
      fetchItemsByTerm: fetchGroups,
      fetchItemById: fetchGroupById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleExpandCollapse = useCallback(
    (isExpanded: boolean) => {
      if (!isExpanded) {
        handleSearch('')
      }
    },
    [handleSearch],
  )

  const handleChange = useCallback(
    (s: number[]) => {
      void changeSearch({
        to: s,
        toIsDirty: true,
      })
    },
    [changeSearch],
  )

  return (
    <Select
      searchable
      variant={SELECT_VARIANT_WITH_CHECKBOX}
      multiple
      value={to}
      options={options}
      loading={loading}
      onChange={handleChange}
      onSearch={handleSearch}
      onExpandCollapse={handleExpandCollapse}
      hint={hint}
      hintNoResults={hintNoResults}
      memoSelectOption
      placeholder={intl.formatMessage({
        id: 'Load.MailupCommon.Select',
      })}
      data-mu-test="groups-overlap__to"
      error={toIsDirty && to.length === 0}
    />
  )
}

export default InputTo
